import type { Chat, HistoriaBasecamp, MarcaImportLegado, OrigenImportado, PersonaVista, TeamMember } from './types'

// Autoría en toda la web (D-25 · migracion/basecamp-completo.md §3).
//
// UNA regla para pintar "quién", en este orden:
//   1. el ROSTER (`team/`): si la persona tiene cuenta, su nombre y foto de Google;
//   2. el DIRECTORIO HISTÓRICO (`personas/`): quien escribió en Basecamp y aún no
//      entra (o ya no está en bee), y los externos con su empresa;
//   3. el ESPEJO guardado en el documento (authorName, byName, userName…);
//   4. la parte local del correo, o "Externo" para una clave bc:.
// El día que alguien entra con Google, el paso 1 empieza a ganar solo y todo su
// histórico se ve con su nombre y su foto, sin migrar nada.
//
// Antes cada pantalla tenía su propia versión (unas con roster, otras con el
// espejo, otras con el correo recortado) y por eso la misma persona salía con dos
// nombres en el mismo proyecto. Puro, para poder probarlo sin React.

/** Clave de un autor externo de Basecamp: `bc:<personId>`. Nunca es un correo. */
export const esClaveExterna = (clave: string | null | undefined): boolean =>
  typeof clave === 'string' && clave.startsWith('bc:')

export interface Directorio {
  /** Roster por correo en minúsculas. */
  roster: Map<string, { nombre: string; foto: string | null }>
  /** Personas históricas por clave (correo en minúsculas o bc:<id>). */
  personas: Map<string, PersonaVista>
}

const normal = (clave: string): string => (esClaveExterna(clave) ? clave : clave.toLowerCase())

export function armarDirectorio(team: TeamMember[], personas: PersonaVista[] = []): Directorio {
  const roster = new Map<string, { nombre: string; foto: string | null }>()
  for (const m of team) roster.set(m.email.toLowerCase(), { nombre: m.name, foto: m.photoUrl ?? null })
  const ps = new Map<string, PersonaVista>()
  for (const p of personas) ps.set(normal(p.clave), p)
  return { roster, personas: ps }
}

export interface Autor {
  clave: string
  /**
   * El correo REAL del autor cuando se conoce (para el tooltip y la foto). null en
   * un externo y en el primer lote, donde la clave guardada es la del importador.
   */
  correo: string | null
  nombre: string
  foto: string | null
  externo: boolean
  empresa: string | null
}

/**
 * Resuelve al autor de un documento.
 *
 * `legado` = el documento es del PRIMER lote (`_import` sin `importado`): ahí la
 * clave guardada es la del importador, no la del autor, y el nombre real solo
 * vive en el espejo. Hasta que el importador v2 lo corrija en sitio, el espejo
 * manda; si no, todo el lote se seguiría viendo "de Damián".
 */
export function resolverAutor(
  dir: Directorio | null,
  clave: string | null | undefined,
  espejo?: string | null,
  legado = false,
): Autor {
  const k = normal(clave ?? '')
  const externo = esClaveExterna(k)
  const persona = dir?.personas.get(k)
  const fallback = (): string =>
    externo ? 'Externo' : (k.split('@')[0] || k || 'Alguien')

  if (legado && espejo?.trim()) {
    return { clave: k, correo: null, nombre: espejo.trim(), foto: null, externo, empresa: null }
  }
  const delRoster = externo ? undefined : dir?.roster.get(k)
  if (delRoster) {
    return { clave: k, correo: k, nombre: delRoster.nombre, foto: delRoster.foto, externo: false, empresa: null }
  }
  const correo = !externo && k.includes('@') ? k : null
  if (persona) {
    return {
      clave: k,
      correo: persona.externo ? null : correo,
      nombre: persona.nombre,
      foto: persona.externo ? null : persona.foto,
      externo: persona.externo || externo,
      empresa: persona.empresa,
    }
  }
  return { clave: k, correo, nombre: espejo?.trim() || fallback(), foto: null, externo, empresa: null }
}

/** "externo · Telcel" (o solo "externo"), o null para un interno. */
export const etiquetaExterno = (a: Pick<Autor, 'externo' | 'empresa'>): string | null =>
  a.externo ? (a.empresa ? `externo · ${a.empresa}` : 'externo') : null

/**
 * Nombres por clave con la MISMA precedencia que resolverAutor (roster sobre
 * personas). Para quien ya trabaja con un Map (chat, menciones): mismo orden sin
 * reescribir cada pantalla.
 */
export function mapaDeNombres(team: TeamMember[], personas: PersonaVista[] = []): Map<string, string> {
  const m = new Map<string, string>()
  for (const p of personas) m.set(normal(p.clave), p.nombre)
  for (const t of team) {
    m.set(t.email, t.name)
    m.set(t.email.toLowerCase(), t.name)
  }
  return m
}

/** Fotos por clave: roster primero; de personas solo internos (externo = iniciales). */
export function mapaDeFotos(team: TeamMember[], personas: PersonaVista[] = []): Map<string, string | null> {
  const m = new Map<string, string | null>()
  for (const p of personas) if (!p.externo && p.foto) m.set(normal(p.clave), p.foto)
  for (const t of team) m.set(t.email.toLowerCase(), t.photoUrl ?? null)
  return m
}

// ---- marca de importación ----

export interface ConMarca {
  importado?: OrigenImportado | null
  _import?: MarcaImportLegado
}

export const esImportado = (doc: ConMarca | null | undefined): boolean =>
  !!doc && (!!doc.importado || !!doc._import)

/** Primer lote sin corregir: la clave guardada es del importador (ver resolverAutor). */
export const esLegado = (doc: ConMarca | null | undefined): boolean =>
  !!doc && !!doc._import && !doc.importado

// ---- historia de Basecamp (archivado allá o de una herramienta apagada) ----
//
// Se migró todo (decisión de Damián, 28-oct), pero lo que Basecamp ya no mostraba no
// debe ensuciar las vistas: va DESPUÉS de lo vivo, en gris y detrás de un botón —el
// mismo patrón de las listas archivadas del tablero, que salen al final y en gris—.
// Lo nativo y lo importado vivo nunca traen estas marcas.

/** ¿Estaba archivado en Basecamp o venía de una herramienta apagada? */
export const esHistoriaBasecamp = (doc: HistoriaBasecamp | null | undefined): boolean =>
  !!doc && (doc.archivadoEnBasecamp === true || doc.herramientaApagadaEnBasecamp === true)

/** Lo vivo y la historia, cada uno en el orden en que venía. */
export function partirHistoria<T extends HistoriaBasecamp>(docs: readonly T[]): { vivos: T[]; historia: T[] } {
  const vivos: T[] = []
  const historia: T[] = []
  for (const d of docs) (esHistoriaBasecamp(d) ? historia : vivos).push(d)
  return { vivos, historia }
}

/** "3 archivados en Basecamp", "1 de una herramienta apagada en Basecamp" o, mezclados, los dos. */
export function textoHistoria(docs: readonly HistoriaBasecamp[]): string {
  const n = docs.length
  const archivados = docs.filter((d) => d.archivadoEnBasecamp === true).length
  if (archivados === n) return `${n} ${n === 1 ? 'archivado' : 'archivados'} en Basecamp`
  if (archivados === 0) return `${n} de ${n === 1 ? 'una herramienta apagada' : 'herramientas apagadas'} en Basecamp`
  return `${n} archivados o de herramientas apagadas en Basecamp`
}

/** Liga al original en Basecamp, si la hay y es https (nunca un javascript:). */
export function urlOriginal(doc: ConMarca | null | undefined): string | null {
  const url = doc?.importado?.url
  if (!url) return null
  try {
    return new URL(url).protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

/**
 * ¿Se pinta "editado"? Solo con una edición REAL (`editedAt`). Lo importado trae
 * sus fechas de Basecamp y saldría editado sin que nadie lo tocara en camp. Los
 * posts NATIVOS anteriores a `editedAt` caen a la regla vieja (updatedAt distinto
 * de createdAt), que desde el arreglo de "fijar no es editar" solo se mueve al
 * editar el contenido.
 */
export function fueEditado(
  doc: ConMarca & { editedAt?: string | null; updatedAt?: string; createdAt?: string },
): boolean {
  if (doc.editedAt) return true
  if (esImportado(doc)) return false
  if (doc.editedAt === null) return false
  return !!doc.updatedAt && !!doc.createdAt && doc.updatedAt !== doc.createdAt
}

/**
 * ¿El chat tiene algo sin leer para mí? "No leído" = el último mensaje es de otro
 * y es posterior a lo leído. Lo leído es lo MÁS NUEVO entre mi `readAt` y el
 * `leidoHasta` del chat: el Campfire importado nace leído para todos, incluso
 * para quien entra después y no tiene `readAt` todavía.
 */
export function chatSinLeer(
  chat: Pick<Chat, 'lastMessage' | 'leidoHasta'>,
  readAt: string | null | undefined,
  meEmail: string,
): boolean {
  if (!chat.lastMessage.text || chat.lastMessage.by === meEmail) return false
  const leido = [readAt, chat.leidoHasta].filter((v): v is string => !!v).sort().pop()
  return !leido || chat.lastMessage.at > leido
}
